<script setup>
/**
 * FeatureGuide — reusable, content-driven in-app guide rendered as a right
 * slide-over. Shared by the Reminders and Pipeline features so the UX (and the
 * "copy curl" behaviour) is not duplicated.
 *
 * The API tab is a Postman/Insomnia-style playground:
 *   - request bar on top (method + editable URL + Send + copy curl);
 *   - left menu of endpoints;
 *   - canvas with Headers / Body sections — the body is a JSON editor with
 *     live syntax highlighting (overlay technique: transparent textarea over a
 *     tokenized <pre>) + JSON lint + one-click formatting;
 *   - live response (status, latency, highlighted JSON).
 *
 * All copy is passed in via the `content` prop (already translated by the
 * caller through vue-i18n). The account API token comes in via `apiToken`
 * (optional) — when absent, the live features degrade gracefully (curl copy
 * still works with a placeholder token).
 */
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEventListener } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import { withApiBase } from './buildApiBase';

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, required: true },
  icon: { type: String, default: 'i-lucide-help-circle' },
  // Resolved API base url ({origin}/api/v1/accounts/{accountId}).
  apiBase: { type: String, default: '' },
  // Real account API token (currentUser.access_token). Optional.
  apiToken: { type: String, default: '' },
  content: { type: Object, required: true },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const activeTabIndex = ref(0);

const tabs = computed(() => [
  { label: props.content.overviewTitle },
  { label: props.content.stepsTitle },
  { label: props.content.apiTitle },
]);

const onTabChange = tab => {
  activeTabIndex.value = tabs.value.findIndex(item => item.label === tab.label);
};

const close = () => emit('close');

// --- API playground state -------------------------------------------------
const endpoints = computed(() => props.content.api ?? []);
const selectedIndex = ref(0);
const requestUrl = ref('');
const requestBody = ref('');
const tokenRevealed = ref(false);
const sending = ref(false);
const response = ref(null); // { status, ok, timeMs, body }
const requestError = ref(null);
// Canvas section: request headers vs request body.
const activeSection = ref('headers');

// Header names/values rendered in the Headers panel (kept in JS so the
// template has no bare string literals).
const tokenHeaderName = 'api_access_token';
const contentTypeHeaderName = 'Content-Type';
const contentTypeValue = 'application/json';
const maskedToken = '••••••••••••••••••••';

const selectedEndpoint = computed(() => endpoints.value[selectedIndex.value]);

const methodOf = endpoint => (endpoint?.url || '').split(' ')[0] || 'GET';
const selectedMethod = computed(() => methodOf(selectedEndpoint.value));
const bodyMethods = ['POST', 'PUT', 'PATCH'];
const hasBody = computed(() => bodyMethods.includes(selectedMethod.value));

const resolveUrl = endpoint => {
  const [, path] = (endpoint?.url || '').split(' ');
  return withApiBase(path ?? endpoint?.url ?? '', props.apiBase);
};

// Security gate: the account token is only ever attached to THIS installation.
// A pasted third-party URL disables Send (otherwise 2 clicks would leak the
// full-access token to an external origin).
const isSameOrigin = computed(() => {
  try {
    return (
      new URL(requestUrl.value, window.location.origin).origin ===
      window.location.origin
    );
  } catch (error) {
    return false;
  }
});

const METHOD_CLASS = {
  GET: 'text-n-teal-11 bg-n-teal-3',
  POST: 'text-n-blue-11 bg-n-blue-3',
  PUT: 'text-n-amber-11 bg-n-amber-3',
  PATCH: 'text-n-amber-11 bg-n-amber-3',
  DELETE: 'text-n-ruby-11 bg-n-ruby-3',
};
const methodClass = method =>
  METHOD_CLASS[method] || 'text-n-slate-11 bg-n-alpha-2';

const statusClass = computed(() => {
  const s = response.value?.status ?? 0;
  if (s >= 200 && s < 300) return 'text-n-teal-11 bg-n-teal-3';
  if (s >= 400 && s < 500) return 'text-n-amber-11 bg-n-amber-3';
  return 'text-n-ruby-11 bg-n-ruby-3';
});

// --- JSON lint + syntax highlighting ---------------------------------------
// Lint state of the request body: empty is fine (sent without a body).
const jsonState = computed(() => {
  const text = requestBody.value.trim();
  if (!hasBody.value || !text) return { valid: true, empty: true, error: null };
  try {
    JSON.parse(text);
    return { valid: true, empty: false, error: null };
  } catch (error) {
    return { valid: false, empty: false, error: error.message };
  }
});

// Tiny JSON tokenizer for highlighting — outputs [{ t: text, c: class }].
// Rendered as spans (no v-html), so it is XSS-safe by construction.
const JSON_TOKEN_CLASSES = {
  key: 'text-n-blue-11',
  string: 'text-n-teal-11',
  number: 'text-n-amber-11',
  boolean: 'text-n-iris-11',
  nil: 'text-n-ruby-11',
  punct: 'text-n-slate-10',
  plain: 'text-n-slate-11',
};

const tokenizeJson = source => {
  const tokens = [];
  const re =
    /("(?:\\.|[^"\\])*")(\s*:)?|-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b|\btrue\b|\bfalse\b|\bnull\b|[{}[\]:,]/g;
  let last = 0;
  let match = re.exec(source);
  while (match) {
    if (match.index > last) {
      tokens.push({
        t: source.slice(last, match.index),
        c: JSON_TOKEN_CLASSES.plain,
      });
    }
    const [full, str, colon] = match;
    if (str) {
      tokens.push({
        t: str,
        c: colon ? JSON_TOKEN_CLASSES.key : JSON_TOKEN_CLASSES.string,
      });
      if (colon) tokens.push({ t: colon, c: JSON_TOKEN_CLASSES.punct });
    } else if (full === 'true' || full === 'false') {
      tokens.push({ t: full, c: JSON_TOKEN_CLASSES.boolean });
    } else if (full === 'null') {
      tokens.push({ t: full, c: JSON_TOKEN_CLASSES.nil });
    } else if (/^[{}[\]:,]$/.test(full)) {
      tokens.push({ t: full, c: JSON_TOKEN_CLASSES.punct });
    } else {
      tokens.push({ t: full, c: JSON_TOKEN_CLASSES.number });
    }
    last = re.lastIndex;
    match = re.exec(source);
  }
  if (last < source.length) {
    tokens.push({ t: source.slice(last), c: JSON_TOKEN_CLASSES.plain });
  }
  // Trailing zero-width space keeps the mirror's height in sync when the body
  // ends with a newline (a bare trailing \n would collapse in the <pre>).
  tokens.push({ t: '​', c: JSON_TOKEN_CLASSES.plain });
  return tokens;
};

const bodyTokens = computed(() => tokenizeJson(requestBody.value));
const responseTokens = computed(() =>
  response.value && !requestError.value ? tokenizeJson(response.value.body) : []
);

// Prettify the body in place (only when it parses).
const formatBody = () => {
  if (!jsonState.value.valid || jsonState.value.empty) return;
  requestBody.value = JSON.stringify(JSON.parse(requestBody.value), null, 2);
};

// Pretty-prints a JSON string; returns it untouched when it doesn't parse.
const prettyJson = source => {
  const text = (source || '').trim();
  if (!text) return '';
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch (error) {
    return source;
  }
};

// Loads an endpoint into the canvas (url + body already formatted) and clears
// the last response.
const selectEndpoint = index => {
  selectedIndex.value = index;
  const endpoint = endpoints.value[index];
  requestUrl.value = resolveUrl(endpoint);
  requestBody.value = prettyJson(endpoint?.body);
  response.value = null;
  requestError.value = null;
  activeSection.value = bodyMethods.includes(methodOf(endpoint))
    ? 'body'
    : 'headers';
};

const resetApiPanel = () => {
  tokenRevealed.value = false;
  if (endpoints.value.length) selectEndpoint(0);
};

// Escapes single quotes for safe interpolation inside '...' shell strings
// (a body like "John's deal" must not break the copied command).
const shellEscape = value => String(value).replace(/'/g, `'\\''`);

// Live curl reflecting the (possibly edited) url + body + real token when available.
const buildCurl = () => {
  const token = props.apiToken || t('GUIDE.COMMON.TOKEN_PLACEHOLDER');
  const lines = [
    `curl -X ${selectedMethod.value} '${shellEscape(requestUrl.value)}' \\`,
    `  -H 'api_access_token: ${shellEscape(token)}' \\`,
  ];
  if (hasBody.value && requestBody.value.trim()) {
    lines.push(`  -H 'Content-Type: application/json' \\`);
    lines.push(`  -d '${shellEscape(requestBody.value)}'`);
  } else {
    lines[lines.length - 1] = lines[lines.length - 1].replace(/ \\$/, '');
  }
  return lines.join('\n');
};

const copyCurl = async () => {
  await copyTextToClipboard(buildCurl());
  useAlert(t('GUIDE.COMMON.COPIED'));
};

const copyToken = async () => {
  if (!props.apiToken) return;
  await copyTextToClipboard(props.apiToken);
  useAlert(t('GUIDE.COMMON.COPIED'));
};

const copyResponse = async () => {
  if (!response.value) return;
  await copyTextToClipboard(response.value.body);
  useAlert(t('GUIDE.COMMON.COPIED'));
};

// Fires the REAL request against the account (Postman-style) and shows the response.
const sendRequest = async () => {
  if (!props.apiToken || !requestUrl.value || sending.value) return;
  if (!jsonState.value.valid || !isSameOrigin.value) return;
  sending.value = true;
  response.value = null;
  requestError.value = null;
  const method = selectedMethod.value;
  const sendBody = hasBody.value && requestBody.value.trim();
  const start = performance.now();
  try {
    const res = await fetch(requestUrl.value, {
      method,
      headers: {
        api_access_token: props.apiToken,
        ...(sendBody ? { 'Content-Type': 'application/json' } : {}),
      },
      body: sendBody ? requestBody.value : undefined,
    });
    const text = await res.text();
    let body = text;
    try {
      body = JSON.stringify(JSON.parse(text), null, 2);
    } catch (parseError) {
      /* keep raw text when not JSON */
    }
    response.value = {
      status: res.status,
      ok: res.ok,
      // Total round-trip incl. body download (not TTFB) — fine for a guide.
      timeMs: Math.round(performance.now() - start),
      body,
    };
  } catch (error) {
    requestError.value = error?.message || t('GUIDE.COMMON.API.SEND_ERROR');
  } finally {
    sending.value = false;
  }
};

// --- Open/close plumbing (unchanged behaviour) ----------------------------
// Guards against the "instant close" race (see git history).
const isReady = ref(false);

const onScrimClick = () => {
  if (isReady.value) close();
};

const onKeydown = event => {
  if (event.key === 'Escape' && isReady.value) close();
};

watch(
  () => props.open,
  async isOpen => {
    isReady.value = false;
    if (!isOpen) return;
    activeTabIndex.value = 0;
    resetApiPanel();
    await nextTick();
    isReady.value = true;
  },
  { immediate: true }
);

useEventListener(document, 'keydown', onKeydown);

onBeforeUnmount(() => {
  isReady.value = false;
});
</script>

<template>
  <TeleportWithDirection to="body">
    <div v-if="open && content" class="flex fixed inset-0 z-50 justify-end">
      <!-- Scrim -->
      <button
        type="button"
        :aria-label="t('GUIDE.COMMON.CLOSE')"
        class="absolute inset-0 border-0 cursor-default bg-n-alpha-black1 backdrop-blur-[4px]"
        @click="onScrimClick"
      />

      <!-- Panel -->
      <aside
        role="dialog"
        aria-modal="true"
        class="flex relative flex-col w-full h-full max-w-3xl shadow-xl bg-n-background ltr:border-l rtl:border-r border-n-weak animate-slide-in-right"
        @click.stop
      >
        <header
          class="flex gap-3 items-center px-6 py-4 border-b border-n-weak shrink-0"
        >
          <span
            class="grid rounded-lg size-9 place-items-center bg-n-alpha-2 text-n-brand"
          >
            <span class="size-5" :class="icon" />
          </span>
          <div class="flex-grow min-w-0">
            <h2 class="text-base font-medium truncate text-n-slate-12">
              {{ title }}
            </h2>
            <p class="text-xs text-n-slate-11">
              {{ t('GUIDE.COMMON.SUBTITLE') }}
            </p>
          </div>
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            :aria-label="t('GUIDE.COMMON.CLOSE')"
            @click="close"
          />
        </header>

        <div class="px-6 py-4 border-b border-n-weak shrink-0">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            @tab-changed="onTabChange"
          />
        </div>

        <div class="flex overflow-hidden flex-col flex-grow min-h-0">
          <!-- Overview -->
          <section
            v-if="activeTabIndex === 0"
            class="flex overflow-y-auto flex-col gap-5 px-6 py-5"
          >
            <p class="text-sm leading-relaxed text-n-slate-12">
              {{ content.overview }}
            </p>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div
                v-for="(useCase, index) in content.useCases"
                :key="index"
                class="flex flex-col gap-2 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2"
              >
                <span
                  class="grid rounded-lg size-8 place-items-center bg-n-alpha-2 text-n-brand"
                >
                  <span class="size-4" :class="useCase.icon" />
                </span>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ useCase.title }}
                </p>
                <p class="text-xs leading-relaxed text-n-slate-11">
                  {{ useCase.description }}
                </p>
              </div>
            </div>
          </section>

          <!-- How to use -->
          <section
            v-else-if="activeTabIndex === 1"
            class="flex overflow-y-auto flex-col gap-3 px-6 py-5"
          >
            <ol class="flex flex-col gap-3 p-0 m-0 list-none">
              <li
                v-for="(step, index) in content.steps"
                :key="index"
                class="flex gap-3 items-start"
              >
                <span
                  class="grid text-xs font-semibold rounded-full shrink-0 size-6 place-items-center bg-n-brand text-white"
                >
                  {{ index + 1 }}
                </span>
                <p class="pt-0.5 text-sm leading-relaxed text-n-slate-12">
                  {{ step }}
                </p>
              </li>
            </ol>
          </section>

          <!-- API playground -->
          <section
            v-else
            class="flex flex-col flex-grow gap-3 px-6 py-5 min-h-0"
          >
            <!-- Request bar (Postman-style): method + URL + actions -->
            <div
              v-if="selectedEndpoint"
              class="flex gap-2 items-center shrink-0"
            >
              <span
                class="px-2.5 py-2 text-[11px] font-bold tracking-wide uppercase rounded-lg shrink-0"
                :class="methodClass(selectedMethod)"
              >
                {{ selectedMethod }}
              </span>
              <input
                v-model="requestUrl"
                :aria-label="t('GUIDE.COMMON.API.URL')"
                spellcheck="false"
                class="flex-grow px-3 py-2 font-mono text-xs rounded-lg outline outline-1 outline-n-weak bg-n-solid-2 text-n-slate-12 focus:outline-n-brand"
              />
              <Button
                v-tooltip.bottom="
                  isSameOrigin
                    ? t('GUIDE.COMMON.API.SEND_HINT')
                    : t('GUIDE.COMMON.API.CROSS_ORIGIN')
                "
                color="blue"
                size="sm"
                icon="i-lucide-send"
                :label="
                  sending
                    ? t('GUIDE.COMMON.API.SENDING')
                    : t('GUIDE.COMMON.API.SEND')
                "
                :is-loading="sending"
                :disabled="
                  !apiToken ||
                  sending ||
                  !jsonState.valid ||
                  !requestUrl ||
                  !isSameOrigin
                "
                @click="sendRequest"
              />
              <Button
                v-tooltip.bottom="t('GUIDE.COMMON.COPY')"
                variant="faded"
                color="slate"
                size="sm"
                icon="i-lucide-terminal"
                :aria-label="t('GUIDE.COMMON.COPY')"
                @click="copyCurl"
              />
            </div>

            <!-- 2-pane: left endpoint menu + canvas -->
            <div class="flex flex-grow gap-4 min-h-0">
              <nav
                class="flex overflow-y-auto flex-col gap-1 pr-2 w-44 border-r shrink-0 border-n-weak"
                :aria-label="t('GUIDE.COMMON.API.ENDPOINTS')"
              >
                <button
                  v-for="(endpoint, index) in endpoints"
                  :key="index"
                  type="button"
                  class="flex flex-col gap-1 items-start px-2 py-1.5 rounded-lg text-start transition-colors"
                  :class="
                    index === selectedIndex
                      ? 'bg-n-alpha-2 text-n-slate-12'
                      : 'text-n-slate-11 hover:bg-n-alpha-1'
                  "
                  @click="selectEndpoint(index)"
                >
                  <span
                    class="px-1 py-0.5 text-[9px] font-semibold tracking-wide uppercase rounded"
                    :class="methodClass(methodOf(endpoint))"
                  >
                    {{ methodOf(endpoint) }}
                  </span>
                  <span class="text-xs leading-tight">
                    {{ endpoint.title }}
                  </span>
                </button>
              </nav>

              <!-- Canvas -->
              <div
                v-if="selectedEndpoint"
                class="flex overflow-y-auto flex-col flex-grow gap-3 min-w-0"
              >
                <p class="text-sm font-medium text-n-slate-12">
                  {{ selectedEndpoint.title }}
                </p>

                <!-- Section switcher + JSON lint badge -->
                <div class="flex gap-1.5 items-center">
                  <button
                    type="button"
                    class="flex gap-1.5 items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                    :class="
                      activeSection === 'headers'
                        ? 'bg-n-alpha-2 text-n-slate-12'
                        : 'text-n-slate-11 hover:bg-n-alpha-1'
                    "
                    @click="activeSection = 'headers'"
                  >
                    <span class="size-3.5 i-lucide-list" />
                    {{ t('GUIDE.COMMON.API.HEADERS') }}
                  </button>
                  <button
                    v-if="hasBody"
                    type="button"
                    class="flex gap-1.5 items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                    :class="
                      activeSection === 'body'
                        ? 'bg-n-alpha-2 text-n-slate-12'
                        : 'text-n-slate-11 hover:bg-n-alpha-1'
                    "
                    @click="activeSection = 'body'"
                  >
                    <span class="size-3.5 i-lucide-braces" />
                    {{ t('GUIDE.COMMON.API.BODY') }}
                  </button>

                  <span class="flex-grow" />

                  <!-- JSON lint badge + prettify -->
                  <template v-if="hasBody && activeSection === 'body'">
                    <span
                      v-if="!jsonState.empty"
                      v-tooltip.top="jsonState.error || ''"
                      class="flex gap-1 items-center px-2 py-0.5 text-[10px] font-semibold rounded-full"
                      :class="
                        jsonState.valid
                          ? 'text-n-teal-11 bg-n-teal-3'
                          : 'text-n-ruby-11 bg-n-ruby-3 cursor-help'
                      "
                    >
                      <span
                        class="size-3"
                        :class="
                          jsonState.valid
                            ? 'i-lucide-check-circle-2'
                            : 'i-lucide-alert-triangle'
                        "
                      />
                      {{
                        jsonState.valid
                          ? t('GUIDE.COMMON.API.VALID_JSON')
                          : t('GUIDE.COMMON.API.INVALID_JSON')
                      }}
                    </span>
                    <Button
                      v-tooltip.top="t('GUIDE.COMMON.API.FORMAT')"
                      variant="ghost"
                      color="slate"
                      size="xs"
                      icon="i-lucide-wand-2"
                      :aria-label="t('GUIDE.COMMON.API.FORMAT')"
                      :disabled="!jsonState.valid || jsonState.empty"
                      @click="formatBody"
                    />
                  </template>
                </div>

                <!-- Headers panel -->
                <div
                  v-if="activeSection === 'headers'"
                  class="flex flex-col rounded-xl outline outline-1 outline-n-weak bg-n-solid-2 divide-y divide-n-weak"
                >
                  <div class="flex gap-2 items-center px-3 py-2">
                    <span class="size-3.5 i-lucide-key-round text-n-brand" />
                    <code class="font-mono text-xs text-n-blue-11">
                      {{ tokenHeaderName }}
                    </code>
                    <code
                      class="flex-grow font-mono text-xs truncate text-n-slate-11"
                    >
                      {{
                        apiToken
                          ? tokenRevealed
                            ? apiToken
                            : maskedToken
                          : t('GUIDE.COMMON.API.NO_TOKEN')
                      }}
                    </code>
                    <Button
                      v-tooltip.top="t('GUIDE.COMMON.API.REVEAL')"
                      variant="ghost"
                      color="slate"
                      size="xs"
                      :icon="
                        tokenRevealed ? 'i-lucide-eye-off' : 'i-lucide-eye'
                      "
                      :aria-label="t('GUIDE.COMMON.API.REVEAL')"
                      :disabled="!apiToken"
                      @click="tokenRevealed = !tokenRevealed"
                    />
                    <Button
                      variant="faded"
                      color="slate"
                      size="xs"
                      icon="i-lucide-copy"
                      :label="t('GUIDE.COMMON.API.COPY_TOKEN')"
                      :disabled="!apiToken"
                      @click="copyToken"
                    />
                  </div>
                  <div
                    v-if="hasBody && requestBody.trim()"
                    class="flex gap-2 items-center px-3 py-2"
                  >
                    <span class="size-3.5 i-lucide-file-json text-n-slate-10" />
                    <code class="font-mono text-xs text-n-blue-11">
                      {{ contentTypeHeaderName }}
                    </code>
                    <code class="flex-grow font-mono text-xs text-n-slate-11">
                      {{ contentTypeValue }}
                    </code>
                  </div>
                  <p class="px-3 py-2 text-[11px] leading-snug text-n-slate-10">
                    {{ content.apiTokenHint }}
                  </p>
                </div>

                <!-- Body editor: transparent textarea over a highlighted mirror -->
                <div
                  v-else
                  class="relative rounded-xl outline outline-1 outline-n-weak bg-n-solid-2 focus-within:outline-n-brand"
                >
                  <pre
                    aria-hidden="true"
                    class="p-3 min-h-[96px] font-mono text-xs leading-relaxed break-words whitespace-pre-wrap pointer-events-none"
                  ><code><span
                    v-for="(token, index) in bodyTokens"
                    :key="index"
                    :class="token.c"
                  >{{ token.t }}</span></code></pre>
                  <textarea
                    v-model="requestBody"
                    :aria-label="t('GUIDE.COMMON.API.REQUEST_BODY')"
                    spellcheck="false"
                    autocapitalize="off"
                    autocomplete="off"
                    class="json-editor-textarea absolute inset-0 p-3 w-full h-full font-mono text-xs leading-relaxed break-words whitespace-pre-wrap bg-transparent border-0 outline-none resize-none overflow-hidden"
                  />
                </div>

                <!-- Response -->
                <div
                  v-if="response || requestError"
                  class="flex flex-col gap-1.5"
                >
                  <div class="flex gap-2 items-center">
                    <span
                      class="flex gap-1.5 items-center text-xs font-medium text-n-slate-11"
                    >
                      <span class="size-3.5 i-lucide-arrow-down-left" />
                      {{ t('GUIDE.COMMON.API.RESPONSE') }}
                    </span>
                    <span
                      v-if="response"
                      class="px-1.5 py-0.5 text-[10px] font-semibold rounded tabular-nums"
                      :class="statusClass"
                    >
                      {{ response.status }}
                    </span>
                    <span v-if="response" class="text-[11px] text-n-slate-10">
                      {{ `${response.timeMs} ms` }}
                    </span>
                    <span class="flex-grow" />
                    <Button
                      v-if="response"
                      v-tooltip.top="t('GUIDE.COMMON.API.COPY_RESPONSE')"
                      variant="ghost"
                      color="slate"
                      size="xs"
                      icon="i-lucide-copy"
                      :aria-label="t('GUIDE.COMMON.API.COPY_RESPONSE')"
                      @click="copyResponse"
                    />
                  </div>
                  <code
                    v-if="requestError"
                    class="block overflow-auto p-3 max-h-64 font-mono text-xs leading-relaxed break-words whitespace-pre-wrap rounded-lg bg-n-background text-n-ruby-11"
                  >
                    {{ requestError }}
                  </code>
                  <pre
                    v-else
                    class="overflow-auto p-3 max-h-64 font-mono text-xs leading-relaxed break-words whitespace-pre-wrap rounded-lg bg-n-background"
                  ><code><span
                    v-for="(token, index) in responseTokens"
                    :key="index"
                    :class="token.c"
                  >{{ token.t }}</span></code></pre>
                </div>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </div>
  </TeleportWithDirection>
</template>

<style scoped>
@keyframes slide-in-right {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.animate-slide-in-right {
  animation: slide-in-right 0.25s ease-out;
}

[dir='rtl'] .animate-slide-in-right {
  animation-name: slide-in-left;
}

@keyframes slide-in-left {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

/* Overlay editor: the textarea text is transparent (the highlighted mirror
   underneath is what the eye sees); only the caret/selection stay visible. */
.json-editor-textarea {
  color: transparent;
  caret-color: #3b82f6;
}
.json-editor-textarea::selection {
  background: rgba(59, 130, 246, 0.25);
}
</style>
